import { Component, inject, OnInit, signal, viewChild } from "@angular/core";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { AdaaHelper } from "../../../core/utils";
import { ConfirmationModalComponent, DataTableComponent } from "../../../shared/components";
import { genericFloatButtons } from "../../../shared/components/general/float-action";
import { Constants } from "../../../shared/constants/constants";
import { AdaaBoolean, PermissionAction } from "../../../shared/constants/enums";
import { CustomButton, ItemAction, TableButtonClicked } from "../../../shared/models";
import { PermissionsService, SystemLayoutService, UsersApiService } from "../../../shared/services";

@Component({
  selector: "adaa-users-list",
  imports: [DataTableComponent],
  templateUrl: "./users-list.html",
  styleUrl: "./users-list.scss",
})
export class UsersListComponent implements OnInit {
  private readonly _toastrService = inject(ToastrService);
  private readonly _translateService = inject(TranslateService);
  private readonly _modalService = inject(NgbModal);
  private readonly _systemLayoutService = inject(SystemLayoutService);
  private readonly _usersApiService = inject(UsersApiService);
  private readonly _router = inject(Router);
  private readonly _permissionsService = inject(PermissionsService);

  private readonly _usersTable = viewChild<DataTableComponent>("usersTable");

  private readonly _genericFloatButtons = genericFloatButtons();
  private readonly _untilDestroyed = AdaaHelper.untilDestroyed();

  readonly Constants = Constants;

  readonly customButtons = signal<CustomButton[]>([]);
  readonly disableDeleteRules = signal<ItemAction[]>([
    {
      operator: "equal",
      propertyName: "id",
      compareWith: AdaaHelper.getLocalStorage(Constants.localStorageKeys.user, {
        type: "prop",
        property: "id",
      }),
    },
  ]);

  public ngOnInit(): void {
    this._floatActions();

    this._systemLayoutService.hasActiveEntityChanged$.pipe(this._untilDestroyed()).subscribe(() => {
      this._usersTable()?.resetFilters();
    });

    this._permissionsService.isPermissionsLoaded.pipe(this._untilDestroyed()).subscribe((loaded: boolean) => {
      if (!loaded) return;

      this._checkPermissions();
    });
  }

  public addNewClicked() {
    this._router.navigateByUrl(`/console/users/create`);
  }

  public actionClicked(action: TableButtonClicked): void {
    const id = action.data.id;
    if (!id) return;

    switch (action.event) {
      case "reset":
        this._resetPassword(id);
        break;
      case "enable":
        this._setUserStatus(id, AdaaBoolean.N);
        break;
      case "disable":
        this._setUserStatus(id, AdaaBoolean.Y);
        break;
      case "delete":
        this._deleteUser(id);
        break;
      case "related":
        this._router.navigateByUrl(`/console/users/related-objects/${id}`);
        break;
      case "view":
        this._router.navigateByUrl(`/console/users/${id}`);
        break;
      case "edit":
        this._router.navigateByUrl(`/console/users/edit/${id}`);
        break;
      default:
        break;
    }
  }

  private _setUserStatus(id: number, status: AdaaBoolean): void {
    this._usersApiService.updateState({ id: id, enabled: status }).subscribe({
      next: (response) => {
        if (response.inError) return;
        this._toastrService.success(
          this._translateService.instant(
            status === AdaaBoolean.Y ? "notification.success.activate" : "notification.success.deactivate"
          )
        );
        this._usersTable()?.loadTableData();
      },
    });
  }

  private _resetPassword(id: number): void {
    const modal = this._modalService.open(ConfirmationModalComponent, {
      centered: true,
      size: "md",
    });

    modal.componentInstance.header = "user.reset_login_attempts";
    modal.componentInstance.title = "user.reset_login_attempts";

    modal.result.then((e) => {
      if (e) this._resetPasswordConfirmed(id);
    });
  }

  private _resetPasswordConfirmed(id: number): void {
    this._usersApiService.resetLoginAttempts(id).subscribe({
      next: (response) => {
        if (response.inError) return;
        else if (response.responseCode === "1") {
          this._toastrService.success(this._translateService.instant("notification.success.save"));
          this._usersTable()?.loadTableData();
        }
      },
    });
  }

  private _deleteUser(id: number): void {
    const modal = this._modalService.open(ConfirmationModalComponent, {
      centered: true,
      size: "md",
    });

    modal.componentInstance.header = "user.create.delete_yes_no_title";
    modal.componentInstance.title = "user.create.delete_yes_no_information";

    modal.result.then((e) => {
      if (e) this._deleteUserConfirmed(id);
    });
  }

  private _deleteUserConfirmed(id: number): void {
    this._usersApiService.delete(id).subscribe({
      next: (response) => {
        if (response.inError) return;

        this._toastrService.success(this._translateService.instant("notification.success.remove"));
        this._usersTable()?.loadTableData();
      },
    });
  }

  private _checkPermissions(): void {
    const managePermission = this._permissionsService.hasPermission([
      {
        permissionAction: PermissionAction.manage,
        objectTypeId: Constants.CONSTANT_PERMISSIONS.USERS,
      },
    ]);

    const viewUserSecurity = this._permissionsService.hasPermission([
      {
        permissionAction: PermissionAction.view,
        objectTypeId: Constants.CONSTANT_PERMISSIONS.USERS_SECURITY,
      },
    ]);

    this.customButtons.set([
      {
        iconName: "adaa-icon-adaa-reset-password",
        eventName: "reset",
        iconTitle: "user.reset_login_attempts",
        onlyIf: () => viewUserSecurity,
      },
      {
        iconName: "adaa-icon-adaa-related-objects",
        eventName: "related",
        iconTitle: "common.form.button.view_related_objects",
        onlyIf: () => managePermission,
      },
      {
        iconName: "adaa-icon-adaa-ok-Y",
        eventName: "enable",
        iconTitle: "common.form.label.active",
        onlyIf: (data: { enabled: AdaaBoolean }) => {
          return AdaaBoolean.Y === data?.enabled && managePermission;
        },
      },
      {
        iconName: "adaa-icon-adaa-ok-N",
        eventName: "disable",
        iconTitle: "common.form.label.inactive",
        onlyIf: (data: { enabled: AdaaBoolean }) => {
          return AdaaBoolean.N === data?.enabled && managePermission;
        },
      },
    ]);
  }

  private _floatActions(): void {
    this._genericFloatButtons([
      {
        key: "print",
        data: undefined,
      },
    ]);
  }
}
